import { useState } from "react";
import { DAY_KEYS, dayLabelLong, normalizeDayKey } from "../utils/days";
import { uid } from "../utils/uid";

/**
 * value:
 * {
 *  enabled:boolean,
 *  hours:string,
 *  groups:[{id,label,days,soup,lines,extras,notes}]
 * }
 */
export default function AdminDailyMenu({ value, onChange }) {
  const v = value || { enabled: true, hours: "11:00–14:30", groups: [] };
  const groups = Array.isArray(v.groups) ? v.groups : [];

  const [selectedId, setSelectedId] = useState(groups[0]?.id || "");
  const activeId = groups.some((g) => g.id === selectedId) ? selectedId : (groups[0]?.id || "");
  const active = groups.find((g) => g.id === activeId) || null;

  const patchRoot = (patch) => onChange({ ...v, ...patch });

  const patchGroup = (id, patch) => {
    const next = groups.map((g) => (g.id === id ? { ...g, ...patch } : g));
    patchRoot({ groups: next });
  };

  const addGroup = () => {
    const id = uid("g");
    const next = [
      ...groups,
      { id, label: `Menu ${groups.length + 1}`, days: [], soup: "", lines: [], extras: [], notes: [] },
    ];
    patchRoot({ groups: next });
    setSelectedId(id);
  };

  const removeGroup = (id) => {
    const next = groups.filter((g) => g.id !== id);
    patchRoot({ groups: next });
    setSelectedId(next[0]?.id || "");
  };

  const toggleDay = (id, dayKey) => {
    const g = groups.find((x) => x.id === id);
    if (!g) return;
    const d = normalizeDayKey(dayKey);
    const days = Array.isArray(g.days) ? g.days : [];
    const has = days.includes(d);
    const nextDays = has ? days.filter((x) => x !== d) : [...days, d];
    patchGroup(id, { days: nextDays });
  };

  const addLine = (id, type) => {
    const g = groups.find((x) => x.id === id);
    const lines = Array.isArray(g?.lines) ? g.lines : [];
    const next = [...lines];

    if (type === "heading") next.push({ type: "heading", text: "Nadpis" });
    if (type === "spacer") next.push({ type: "spacer" });
    if (type === "item") next.push({ type: "item", text: "Položka", priceSolo: 145, priceWithSoup: 165 });

    patchGroup(id, { lines: next });
  };

  const updateLine = (id, idx, patch) => {
    const g = groups.find((x) => x.id === id);
    const lines = Array.isArray(g?.lines) ? g.lines : [];
    const next = lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln));
    patchGroup(id, { lines: next });
  };

  const removeLine = (id, idx) => {
    const g = groups.find((x) => x.id === id);
    const lines = Array.isArray(g?.lines) ? g.lines : [];
    const next = lines.filter((_, i) => i !== idx);
    patchGroup(id, { lines: next });
  };

  const moveLine = (id, idx, dir) => {
    const g = groups.find((x) => x.id === id);
    const lines = Array.isArray(g?.lines) ? g.lines : [];
    const j = idx + dir;
    if (j < 0 || j >= lines.length) return;
    const next = [...lines];
    [next[idx], next[j]] = [next[j], next[idx]];
    patchGroup(id, { lines: next });
  };

  const addExtra = (id) => {
    const g = groups.find((x) => x.id === id);
    const extras = Array.isArray(g?.extras) ? g.extras : [];
    patchGroup(id, { extras: [...extras, { text: "Doplňek", price: 10 }] });
  };

  const updateExtra = (id, idx, patch) => {
    const g = groups.find((x) => x.id === id);
    const extras = Array.isArray(g?.extras) ? g.extras : [];
    patchGroup(id, { extras: extras.map((x, i) => (i === idx ? { ...x, ...patch } : x)) });
  };

  const removeExtra = (id, idx) => {
    const g = groups.find((x) => x.id === id);
    const extras = Array.isArray(g?.extras) ? g.extras : [];
    patchGroup(id, { extras: extras.filter((_, i) => i !== idx) });
  };

  const addNote = (id) => {
    const g = groups.find((x) => x.id === id);
    const notes = Array.isArray(g?.notes) ? g.notes : [];
    patchGroup(id, { notes: [...notes, "Poznámka"] });
  };

  const updateNote = (id, idx, text) => {
    const g = groups.find((x) => x.id === id);
    const notes = Array.isArray(g?.notes) ? g.notes : [];
    patchGroup(id, { notes: notes.map((n, i) => (i === idx ? text : n)) });
  };

  const removeNote = (id, idx) => {
    const g = groups.find((x) => x.id === id);
    const notes = Array.isArray(g?.notes) ? g.notes : [];
    patchGroup(id, { notes: notes.filter((_, i) => i !== idx) });
  };

  return (
    <div className="adminDM">
      <div className="adminDMHeader">
        <div className="adminMiniField">
          <span>Zobrazení</span>
          <select
            className="adminInlineInput"
            value={v.enabled ? "on" : "off"}
            onChange={(e) => patchRoot({ enabled: e.target.value === "on" })}
          >
            <option value="on">Zapnuto</option>
            <option value="off">Vypnuto</option>
          </select>
        </div>

        <div className="adminMiniField">
          <span>Čas (text)</span>
          <input
            className="adminInlineInput"
            value={v.hours || ""}
            onChange={(e) => patchRoot({ hours: e.target.value })}
            placeholder="11:00–14:30"
          />
        </div>

        <button className="adminSaveBtn" type="button" onClick={addGroup}>
          + Přidat skupinu
        </button>
      </div>

      {!v.enabled ? (
        <div className="placeholder">Denní menu je vypnuto.</div>
      ) : (
        <div className="adminDMGrid">
          {/* LEFT groups */}
          <div className="adminDMLeft">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                className={`adminGroupBtn ${g.id === activeId ? "isActive" : ""}`}
                onClick={() => setSelectedId(g.id)}
              >
                <div style={{ fontWeight: 900 }}>{g.label || "Menu"}</div>
                <div style={{ opacity: 0.75, fontSize: 12 }}>
                  {Array.isArray(g.days) && g.days.length ? g.days.join(", ") : "bez dnů"}
                </div>
              </button>
            ))}
          </div>

          {/* RIGHT editor */}
          <div className="adminDMRight">
            {!active ? (
              <div className="placeholder">Vyber skupinu vlevo.</div>
            ) : (
              <div className="adminCard">
                <div className="adminRow2">
                  <div>
                    <div className="adminLabel">Název skupiny</div>
                    <input
                      className="adminInlineInput"
                      value={active.label || ""}
                      onChange={(e) => patchGroup(active.id, { label: e.target.value })}
                      placeholder="Menu A"
                    />
                  </div>

                  <div>
                    <div className="adminLabel">Polévka (text)</div>
                    <input
                      className="adminInlineInput"
                      value={active.soup || ""}
                      onChange={(e) => patchGroup(active.id, { soup: e.target.value })}
                      placeholder="Polévka: kuřecí / rajčatová / čočková"
                    />
                  </div>
                </div>

                <div className="adminLabel" style={{ marginTop: 12 }}>Dny (klikáním vybíráš)</div>
                <div className="adminDays">
                  {DAY_KEYS.map((k) => (
                    <label key={k} className="adminDay">
                      <input
                        type="checkbox"
                        checked={Array.isArray(active.days) ? active.days.includes(k) : false}
                        onChange={() => toggleDay(active.id, k)}
                      />
                      <span>{dayLabelLong(k)}</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  <button className="adminSaveBtn adminSmallBtn" type="button" onClick={() => addLine(active.id, "item")}>
                    + Položka
                  </button>
                  <button className="adminSaveBtn adminSmallBtn" type="button" onClick={() => addLine(active.id, "heading")}>
                    + Nadpis
                  </button>
                  <button className="adminSaveBtn adminSmallBtn" type="button" onClick={() => addLine(active.id, "spacer")}>
                    + Mezera
                  </button>

                  <button
                    className="adminDangerBtn"
                    type="button"
                    onClick={() => removeGroup(active.id)}
                    style={{ marginLeft: "auto" }}
                  >
                    Smazat skupinu
                  </button>
                </div>

                {/* Lines */}
                <div style={{ marginTop: 12 }}>
                  {(active.lines || []).map((ln, idx) => (
                    <div key={idx} className="adminLine">
                      <div className="adminLineLeft">
                        <div className="adminLineType">{ln.type}</div>

                        {ln.type === "heading" ? (
                          <input
                            className="adminInlineInput"
                            value={ln.text || ""}
                            onChange={(e) => updateLine(active.id, idx, { text: e.target.value })}
                            placeholder="Nadpis"
                          />
                        ) : null}

                        {ln.type === "item" ? (
                          <div className="adminItemGrid">
                            <input
                              className="adminInlineInput"
                              value={ln.text || ""}
                              onChange={(e) => updateLine(active.id, idx, { text: e.target.value })}
                              placeholder="Text položky"
                            />
                            <input
                              className="adminInlineInput"
                              type="number"
                              value={Number.isFinite(ln.priceSolo) ? ln.priceSolo : ""}
                              onChange={(e) => updateLine(active.id, idx, { priceSolo: Number(e.target.value) })}
                              placeholder="145"
                            />
                            <input
                              className="adminInlineInput"
                              type="number"
                              value={Number.isFinite(ln.priceWithSoup) ? ln.priceWithSoup : ""}
                              onChange={(e) => updateLine(active.id, idx, { priceWithSoup: Number(e.target.value) })}
                              placeholder="165"
                            />
                          </div>
                        ) : null}

                        {ln.type === "spacer" ? (
                          <div className="adminHint">Tahle položka dělá jen mezeru mezi bloky.</div>
                        ) : null}
                      </div>

                      <div className="adminLineBtns">
                        <button className="adminIconBtn" type="button" onClick={() => moveLine(active.id, idx, -1)} title="Nahoru">
                          ↑
                        </button>
                        <button className="adminIconBtn" type="button" onClick={() => moveLine(active.id, idx, +1)} title="Dolů">
                          ↓
                        </button>
                        <button className="adminIconBtn danger" type="button" onClick={() => removeLine(active.id, idx)} title="Smazat">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Extras */}
                <div style={{ marginTop: 14 }}>
                  <div className="dmHeading">Doplňky</div>
                  {(active.extras || []).map((x, i) => (
                    <div key={i} className="adminLine">
                      <div className="adminLineLeft">
                        <div className="adminItemGrid">
                          <input
                            className="adminInlineInput"
                            value={x.text || ""}
                            onChange={(e) => updateExtra(active.id, i, { text: e.target.value })}
                            placeholder="česneková placka s máslem"
                          />
                          <input
                            className="adminInlineInput"
                            type="number"
                            value={Number.isFinite(x.price) ? x.price : ""}
                            onChange={(e) => updateExtra(active.id, i, { price: Number(e.target.value) })}
                            placeholder="10"
                          />
                          <div />
                        </div>
                      </div>
                      <div className="adminLineBtns">
                        <button className="adminIconBtn danger" type="button" onClick={() => removeExtra(active.id, i)}>
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  <button className="adminSaveBtn adminSmallBtn" type="button" onClick={() => addExtra(active.id)}>
                    + Přidat doplněk
                  </button>
                </div>

                {/* Notes */}
                <div style={{ marginTop: 14 }}>
                  <div className="dmHeading">Poznámky</div>
                  {(active.notes || []).map((n, i) => (
                    <div key={i} className="adminLine">
                      <div className="adminLineLeft">
                        <input
                          className="adminInlineInput"
                          value={n}
                          onChange={(e) => updateNote(active.id, i, e.target.value)}
                          placeholder="Ke každému jídlu rýže nebo placka / půl napůl zdarma"
                        />
                      </div>
                      <div className="adminLineBtns">
                        <button className="adminIconBtn danger" type="button" onClick={() => removeNote(active.id, i)}>
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  <button className="adminSaveBtn adminSmallBtn" type="button" onClick={() => addNote(active.id)}>
                    + Přidat poznámku
                  </button>
                </div>

                <div className="adminHint" style={{ marginTop: 12 }}>
                  Tip: skupiny si můžeš udělat jakkoliv (Po–St, Čt–Pá, nebo třeba „Speciál“ bez dnů).
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
